import { useEffect, useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import { ContentPageHeader } from "../common/content-page-header/content-page-header";
import { CardContainer } from "../common/card-container/card-container";
import { LoadingIndicator } from "../common/loading-indicator/loading-indicator";
import { resources } from "@constants/hardcoded-strings";
import { ContentPageBody } from "../common/content-page-body/content-page-body";
import { useDispatch, useSelector } from "react-redux";
import {
    getAlertEventsReferenceDataReduxRequest,
    getAlertEventsReduxRequest
} from "@redux-requests/alert-events";
import { AlertEventsSearchForm } from "./alert-events-search-form/alert-events-search-form";
import { AlertEventsTableContainer } from "./alert-events-table-container/alert-events-table-container";
import { downloadAlertEventsFile } from "@utils/file-util";
import { AlertEventsSearchFiltersState } from "@idw-types/state/alert-events-state";
import { setAlertEventsSearchFilters } from "@redux/actions/alert-events-actions";
import { resetTableSettingsTableState } from "@redux/actions/table-settings-actions";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { initialAlertEventsState } from "@redux/reducers/alert-events-reducer";
import { SERVER_DATE_FORMAT, SERVER_DATE_TIME_FORMAT } from "@constants/date-formats";
import { TimeRangeTypes } from "@enums/time-range-types";
import parse from "date-fns/parse";
import format from "date-fns/format";
import startOfDay from "date-fns/startOfDay";
import endOfDay from "date-fns/endOfDay";
import { createSearchParams, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { FormattedAlertEventHistory } from "@idw-types/data/alert-event-history";
import { navigateToUtilWithQueryParameters } from "@utils/navigation-util";
import { REPORTS_TRANSACTIONS_URL } from "@constants/routes";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { getAlertEventsState, getTableSettingsState } from "@redux/selectors/root-selectors";

export const AlertEventsContainer = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const dispatch = useDispatch();

    const { alertEvents: alertEventsTableState } = useSelector(getTableSettingsState);
    const { referenceData, searchFilters, searchResults } = useSelector(getAlertEventsState);

    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getAlertEventsReferenceDataReduxRequest(dispatch)();
        }
    }, [referenceData.isLoading, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.alerts &&
            referenceData.companies &&
            referenceData.rpServices &&
            referenceData.workflows &&
            referenceData.assertions &&
            referenceData.attributeTypes &&
            referenceData.policies
        );
    }, [
        referenceData.alerts,
        referenceData.companies,
        referenceData.rpServices,
        referenceData.workflows,
        referenceData.assertions,
        referenceData.attributeTypes,
        referenceData.policies
    ]);

    const isLoading = useMemo(() => {
        return referenceData.isLoading;
    }, [referenceData.isLoading]);

    const isLoaded = useMemo(() => {
        return !isLoading && isReferenceDataPresent && !shouldLoadReferenceData;
    }, [isLoading, isReferenceDataPresent, shouldLoadReferenceData]);

    useEffect(() => {
        if (Array.from(searchParams.entries()).length > 0 && isLoaded) {
            const shouldSearchOnLoad = isStringTrue(searchParams.get("shouldSearchOnLoad"));

            const newSearchFilters: AlertEventsSearchFiltersState = {
                companyIds: searchParams.get("companyIds")?.split(",") || initialAlertEventsState.searchFilters.companyIds,
                alertIds: searchParams.get("alertIds")?.split(",") || initialAlertEventsState.searchFilters.alertIds,
                timeRangeType: (searchParams.get("timeRangeType") || initialAlertEventsState.searchFilters.timeRangeType) as TimeRangeTypes,
                startDate: searchParams.has("startDate")
                    ? parse(searchParams.get("startDate"), SERVER_DATE_FORMAT, new Date())
                    : initialAlertEventsState.searchFilters.startDate,
                endDate: searchParams.has("endDate")
                    ? parse(searchParams.get("endDate"), SERVER_DATE_FORMAT, new Date())
                    : initialAlertEventsState.searchFilters.endDate
            };

            if (shouldSearchOnLoad) {
                handleSearch(newSearchFilters);
            } else {
                dispatch(setAlertEventsSearchFilters(newSearchFilters));
            }
        }
    }, [searchParams, isLoaded]);

    const handleSearch = (newSearchFilters: AlertEventsSearchFiltersState) => {
        const { companyIds, alertIds, timeRangeType, startDate, endDate } = newSearchFilters;

        dispatch(resetTableSettingsTableState(TableSettingsTableTypes.ALERT_EVENTS));
        dispatch(setAlertEventsSearchFilters({
            companyIds,
            alertIds,
            timeRangeType,
            startDate,
            endDate
        }));

        getAlertEventsReduxRequest(dispatch)(
            companyIds,
            alertIds,
            startDate,
            endDate,
            referenceData.alerts,
            referenceData.companies,
            referenceData.rpServices,
            referenceData.workflows,
            referenceData.assertions,
            referenceData.attributeTypes,
            referenceData.policies
        );
    };

    const handleClickOnTableCellLink = (alertEvent: FormattedAlertEventHistory) => {
        const queryParametersObject = {
            startDate: format(startOfDay(searchFilters.startDate), SERVER_DATE_TIME_FORMAT),
            endDate: format(endOfDay(searchFilters.endDate), SERVER_DATE_TIME_FORMAT),
            timeRangeType: TimeRangeTypes.CUSTOM,
            shouldSearchOnLoad: "true",
            transactionId: ""
        };

        if (alertEvent.cause?.asis?.length) {
            queryParametersObject.transactionId = alertEvent.cause.asis.join(",");
        } else {
            delete queryParametersObject.transactionId;
        }

        const queryParameters = createSearchParams(queryParametersObject).toString();
        navigateToUtilWithQueryParameters(REPORTS_TRANSACTIONS_URL, navigate, routerLocation.pathname, queryParameters);
    };

    const headerButton = useMemo(() => {
        const onClick = () => {
            downloadAlertEventsFile("Alert_Event_Report_Export", searchResults.alertEvents);
        };

        return (
            <Button
                id="alert-events-export-to-csv-button"
                title={resources.common.buttons.exportToCSV.label}
                onClick={onClick}
                disabled={!isLoaded || searchResults.isLoading || !searchResults.alertEvents?.length}
            >
                {resources.common.buttons.exportToCSV.label}
            </Button>
        );
    }, [isLoaded, searchResults.isLoading, searchResults.alertEvents]);

    return (
        <CardContainer isPageHeight={true}>
            <ContentPageHeader title={resources.pages.alertEvents.header} headerButton={headerButton} />
            <ContentPageBody isMaxWidth={true} isPageHeight={true}>
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
                {isLoaded && (
                    <AlertEventsSearchForm
                        requestInProgress={searchResults.isLoading}
                        alerts={referenceData.alerts}
                        companies={referenceData.companies}
                        searchFilters={searchFilters}
                        handleSearch={handleSearch}
                    />
                )}
                {isLoaded && !searchResults.isLoading && searchResults.alertEvents && (
                    <AlertEventsTableContainer
                        alertEvents={searchResults.alertEvents}
                        tableState={alertEventsTableState}
                        isPageHeight={true}
                        handleClickOnTableCellLink={handleClickOnTableCellLink}
                    />
                )}
                {isLoaded && searchResults.isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};